/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import {
  SkeletonCircle,
  SkeletonText,
} from 'src/components/ts-components/SkeletonBlock'

/** One row of four cards at desktop width — what a default stat set fills. */
const CARD_COUNT = 4
/** The leader (#1) row, then the runners-up listed under it. */
const RUNNER_UP_COUNT = 3

/**
 * Stands in for the Leaders grid on a cold load: the same 4 / 2 / 1-column
 * grid of cards, each a title row, the #1 leader with a large avatar, and the
 * runners-up beneath — so the cards land in place rather than replacing a
 * centered spinner.
 */
const LeadersSkeleton = () => {
  'use memo'

  const cards = Array.from({ length: CARD_COUNT })
  const runnersUp = Array.from({ length: RUNNER_UP_COUNT })

  return (
    <div css={gridStyle} aria-hidden='true' data-testid='leaders-skeleton'>
      {cards.map((_, cardIndex) => (
        <div css={cardStyle} key={cardIndex}>
          <div css={titleRowStyle}>
            <SkeletonText width={96} height={12} />
            <SkeletonText width={56} height={10} />
          </div>
          <div css={panelStyle}>
            <div css={leaderRowStyle}>
              <SkeletonCircle size={56} />
              <div css={nameStackStyle}>
                <SkeletonText width='70%' height={14} />
                <SkeletonText width='50%' height={10} />
              </div>
              <SkeletonText width={24} height={20} />
            </div>
            {runnersUp.map((__, rowIndex) => (
              <div css={runnerUpRowStyle} key={rowIndex}>
                <SkeletonCircle size={32} />
                <div css={nameStackStyle}>
                  <SkeletonText width='60%' height={12} />
                  <SkeletonText width='40%' height={10} />
                </div>
                <SkeletonText width={20} height={12} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

export default LeadersSkeleton

const gridStyle = css`
  margin-bottom: 24px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 0 16px;
  }
`

const cardStyle = css`
  min-height: 300px;
  width: 100%;
  border-radius: 8px;
`

const titleRowStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
`

const panelStyle = css`
  border: 1px solid ${colors.SECONDARY_300};
  border-radius: 8px;
  overflow: hidden;
`

const leaderRowStyle = css`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
`

const runnerUpRowStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
`

const nameStackStyle = css`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`
